import React, { useState } from 'react';
import './IntroModel.css';
import { Link, useLocation } from 'react-router-dom';

/**
 * IntroModal (Future Family AI)
 * Shown once per browser session (or once ever, if you switch to localStorage)
 * when the app first loads. Presents the Future Family AI intro / how-it-works
 * copy in a T&C-style popup with an "I Agree" / "Get Started" action.
 *
 * The modal is hidden on the Terms and Privacy pages so users can actually
 * read them after clicking the links inside the modal. It is NOT marked as
 * "seen" while hidden, so it comes back when they navigate to another page.
 *
 * Optional prop: onScheduleAppointment — called when the user clicks
 * "Schedule a Consultation". Wire this to your appointments route/modal.
 *
 * Usage: render <IntroModal /> near the top of App.js, above <Routes>.
 */

const STORAGE_KEY = 'ff_intro_seen';

// Pages where the intro modal must never cover the content
const HIDDEN_ON_PATHS = ['/terms-and-conditions', '/privacy-policy'];

const IntroModal = ({ onScheduleAppointment }) => {
  const { pathname } = useLocation();

  const [visible, setVisible] = useState(() => {
    try {
      return !sessionStorage.getItem(STORAGE_KEY);
    } catch {
      return true;
    }
  });
  const [agreed, setAgreed] = useState(false);

  // Normalize (lowercase, no trailing slash) before comparing
  const currentPath = pathname.toLowerCase().replace(/\/+$/, '') || '/';
  const isHiddenPage = HIDDEN_ON_PATHS.includes(currentPath);

  if (!visible || isHiddenPage) return null;

  const handleClose = () => {
    try {
      sessionStorage.setItem(STORAGE_KEY, 'true');
    } catch {
      /* ignore storage errors (e.g. private browsing) */
    }
    setVisible(false);
  };

  const handleScheduleClick = () => {
    handleClose();
    if (typeof onScheduleAppointment === 'function') {
      onScheduleAppointment();
    }
  };

  return (
    <div className="intro-modal-overlay" role="dialog" aria-modal="true" aria-labelledby="intro-modal-title">
      <div className="intro-modal">
        <div className="intro-modal-scroll">
          <h1 id="intro-modal-title" className="intro-modal-brand">Future Family</h1>
          <p className="intro-modal-tagline">Plan Your Family With Clarity and Confidence</p>

          <p>
            Future Family AI brings together wearable health tracking, AI-read
            genetic insight, and access to licensed medical professionals, so
            you can make informed decisions about starting or growing your
            family.
          </p>

          <h2>How It Works</h2>
          <p>
            Tell us where you are in your journey — Planning Pregnancy, Currently
            Pregnant, an IVF Journey, Egg Donation, Surrogacy, or a Fertility
            Consultation. We use that one answer to personalize your dashboard,
            your care milestones, and the resources we connect you with,
            including real doctors.
          </p>

          <h3>Stage 1 — Get Started</h3>
          <ol className="intro-modal-steps">
            <li>Create your account and complete your profile — basics, lifestyle, and contact details.</li>
            <li>Set your family-planning goal so the app tailors itself to your situation.</li>
            <li>Connect a wearable (Smart Watch) to start tracking your vitals.</li>
            <li>
              Your dashboard turns heart rate, HRV, respiratory rate, movement, and
              blood pressure into a clear, plain-language health trend summary —
              not just raw numbers.
            </li>
            <li>Milestones and reminders get added automatically to help keep your care plan on track.</li>
          </ol>

          <h3>Stage 2 — Health Monitoring (Ongoing)</h3>
          <p>
            Your dashboard brings live vitals, trend charts, and uploaded health
            reports together in one place, so you and your doctor are always
            looking at the same picture.
          </p>

          <h3>Stage 3 — Couple DNA &amp; Birth Risk (Optional)</h3>
          <p>
            If you and your partner have carrier-screening or genetic-testing
            reports, you can upload both. <strong>Our AI reads them in full and
            cross-analyzes them for shared-carrier risk to a future child</strong> —
            every finding is labeled by confidence, never presented as more
            certain than it is.
          </p>

          <h3>Stage 4 — Doctor Consultation</h3>
          <p>
            DNA and health results are a starting point, not a diagnosis.
            Future Family AI lets you book a consultation with a licensed
            physician or genetic counselor to review your results, answer
            questions, and discuss next steps for family planning.
          </p>
          <ul className="intro-modal-why">
            <li>🩺 Talk through your results with a real doctor.</li>
            <li>📅 Book a video, phone, or in-person appointment — shown in your own time zone.</li>
            <li>🧬 Get guidance on your Couple DNA &amp; Birth Risk findings from a genetic counselor.</li>
            <li>🔒 Your medical information stays private and is never shared without consent.</li>
          </ul>

          <h2>Why Future Family AI?</h2>
          <ul className="intro-modal-why">
            <li>❤️ One Connected Platform — health tracking, genetics, and doctor care, not five separate apps.</li>
            <li>🧠 Personalized to Your Goal — your dashboard and care plan adapt to your specific journey.</li>
            <li>🧬 Genetic Insight You Can Read — dense carrier-screening reports, in plain language.</li>
            <li>🩺 Real Medical Support — book time with licensed doctors and genetic counselors.</li>
            <li>🔒 You Stay in Control — your data is only ever shared with a doctor you've actually booked.</li>
          </ul>

          <p className="intro-modal-disclaimer">
            Future Family AI provides health tracking and genetic insights for
            informational purposes — not a medical diagnosis. The Couple DNA
            &amp; Birth Risk feature is AI-assisted and supplements, but never
            replaces, professional genetic counseling. Please consult a
            licensed doctor or genetic counselor, including through the
            consultation feature above, before making medical or
            family-planning decisions based on your results.
          </p>

          <label className="intro-modal-agree">
            <input
              type="checkbox"
              checked={agreed}
              onChange={(e) => setAgreed(e.target.checked)}
            />
            I have read and agree to the <Link to="/terms-and-conditions">Terms of Service </Link>and <Link to="/privacy-policy">Privacy Policy</Link>.
          </label>
        </div>

        <div className="intro-modal-actions">
          <button
            className="intro-modal-btn"
            disabled={!agreed}
            onClick={handleClose}
          >
            Get Started
          </button>
        </div>
      </div>
    </div>
  );
};

export default IntroModal;